// frontend/src/components/detail/tables/PreviousSolutionsTable.jsx
import React from 'react';
import { Box, Typography, IconButton, Tooltip, Link as MuiLink } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { DataTable } from '../../tablekit';
import { slugify } from '../../../utils/slug';

/* -------------------- width map (edit here) -------------------- */
const W = {
  parent: {
    prevCategory: { minWidth: 260 },              // Previous Solution
    occurrences: { width: 140, align: 'right' },  // Occurrences
    open: { width: 64, align: 'center' },         // Open
  },
  reasons: {
    category: { minWidth: 320 },                  // Reason
    count: { width: 140, align: 'right' },        // Occurrences
    // Nested "Interviews with this reason" table column widths
    nested: {
      name: { width: 160 },
      date: { width: 120 },
      prevSolution: { minWidth: 220 },
      reason: { minWidth: 260 },
      quote: { minWidth: 320 },
      open: { width: 64, align: 'center' },
    }
  },
  interviews: {
    name: { width: 160 },                     // Name
    date: { width: 120 },                     // Date
    prevSolution: { minWidth: 220 },          // Previous Solution (raw)
    reasonsList: { minWidth: 320 },           // Reasons Not Good Enough
    open: { width: 64, align: 'center' }      // Open
  }
};

/* ----------------------------- helpers ----------------------------- */

function formatSegmentation(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const parts = Object.entries(seg).map(([k, v]) => {
    if (v == null) return null;
    if (Array.isArray(v)) return `${k}: ${v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')}`;
    if (typeof v === 'object') {
      const inner = Object.entries(v).map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`).join('; ');
      return `${k}: { ${inner} }`;
    }
    return `${k}: ${String(v)}`;
  }).filter(Boolean);
  return parts.length ? parts.join(' • ') : '—';
}

const pickPrevSolutionCategory = (s) =>
  s?.previous_solution_category || s?.previous_solution || s?.previousSolutionCategory || '';

const pickPrevSolutionRaw = (s) =>
  s?.previous_solution || s?.previousSolution || s?.previous_solution_text || '';

const getReasonsArray = (s) => {
  const arr = s?.reasons_not_good_enough || s?.reasonsNotGoodEnough || [];
  return Array.isArray(arr) ? arr : [];
};

const reasonCatOf = (r) =>
  r?.reason_not_good_enough_category || r?.reasonCategory || r?.category || r?.reason || '';

const reasonRawOf = (r) => r?.reason_not_good_enough || r?.reason || r?.text || '';

const reasonQuoteOf = (r) => r?.reason_not_good_enough_quote || r?.quote || '';

/** Build grouped rows (no initial filtering; page context already filtered) */
function buildGroups(stories = []) {
  const groups = new Map(); // prevSolSlug -> { prevCategory, count, reasons: Map, interviews: [] }

  for (const s of stories) {
    const prevCat = String(pickPrevSolutionCategory(s) || '').trim();
    if (!prevCat) continue;

    const prevSlug = slugify(prevCat);
    if (!groups.has(prevSlug)) {
      groups.set(prevSlug, {
        prevCategory: prevCat,
        count: 0,
        reasons: new Map(), // key -> { category, count, instances: [...] }
        interviews: []
      });
    }
    const g = groups.get(prevSlug);
    g.count += 1;

    // Collect reasons and keep all matching interview instances for each reason
    for (const r of getReasonsArray(s)) {
      const cat = String(reasonCatOf(r) || '').trim() || 'Uncategorized';
      const key = slugify(cat);
      if (!g.reasons.has(key)) g.reasons.set(key, { category: cat, count: 0, instances: [] });
      const rr = g.reasons.get(key);
      rr.count += 1;

      rr.instances.push({
        name: s.customer_name || 'Interview',
        date: s.conversation_date || '',
        prevSolution: pickPrevSolutionRaw(s) || prevCat,
        reason: reasonRawOf(r) || '',
        quote: reasonQuoteOf(r) || '',
        href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
        segmentation: formatSegmentation(s.segmentation_data),
      });
    }

    // Interviews table row for the "All interviews with this previous solution" sub-table
    g.interviews.push({
      name: s.customer_name || 'Interview',
      date: s.conversation_date || '',
      prevSolution: pickPrevSolutionRaw(s) || prevCat,
      reasonsList: getReasonsArray(s).map(rr => reasonRawOf(rr)).filter(Boolean),
      href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
      segmentation: formatSegmentation(s.segmentation_data),
    });
  }

  return [...groups.values()]
    .map(g => ({
      key: slugify(g.prevCategory),
      prevCategory: g.prevCategory,
      occurrences: g.count,
      // reasons as array (sorted by count desc)
      reasons: [...g.reasons.values()].sort((a, b) => b.count - a.count || a.category.localeCompare(b.category)),
      // interviews table: newest first
      interviews: g.interviews.sort((a, b) => (Date.parse(b.date || '') || 0) - (Date.parse(a.date || '') || 0)),
    }))
    .sort((a, b) => b.occurrences - a.occurrences || a.prevCategory.localeCompare(b.prevCategory));
}

/* ----------------------------- component ----------------------------- */

export default function PreviousSolutionsTable({ customerStories = [], title = 'Previous Solutions' }) {
  const location = useLocation();
  const rows = React.useMemo(() => buildGroups(customerStories), [customerStories]);

  if (!rows.length) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No previous solutions found for this product.</Typography>;
  }

  /* ---------- Parent columns ---------- */
  const columns = [
    { key: 'prevCategory', header: 'Previous Solution', ...W.parent.prevCategory },
    { key: 'occurrences', header: 'Occurrences', type: 'number', align: 'right', ...W.parent.occurrences },
    {
      key: 'open',
      header: 'Open',
      accessor: (r) => `/previous-solution/${slugify(r.prevCategory)}${location.search || ''}`,
      align: 'center',
      render: (href) => (
        <Tooltip title="Open previous solution details">
          <IconButton size="small" component={RouterLink} to={href} aria-label="Open previous solution details">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      ...W.parent.open
    }
  ];

  /* ---------- Sub-tables ---------- */
  const subTables = [
    // A) Reasons not chosen (with row-expansion to show "Interviews with this reason")
    {
      key: 'reasons',
      title: 'Reasons not chosen',
      columns: [
        { key: 'category', header: 'Reason', ...W.reasons.category },
        { key: 'count', header: 'Occurrences', type: 'number', align: 'right', ...W.reasons.count },
      ],
      // NEW: use DataTable's subtable row-expansion to render the nested table BELOW the reasons table
      renderExpanded: (reasonRow /* child row */, parentRow /* parent */) => {
        const nestedColumns = [
          { key: 'name', header: 'Name', ...W.reasons.nested.name },
          { key: 'date', header: 'Date', type: 'date', ...W.reasons.nested.date },
          { key: 'prevSolution', header: 'Previous Solution (raw)', ...W.reasons.nested.prevSolution },
          { key: 'reason', header: 'Reason Not Good Enough (raw)', ...W.reasons.nested.reason },
          {
            key: 'quote',
            header: 'Quote',
            accessor: (r) => r.quote || '—',
            render: (text) =>
              text ? <MuiLink href="#" underline="hover">{text}</MuiLink>
                   : <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>,
            ...W.reasons.nested.quote
          },
          {
            key: 'href',
            header: 'Open',
            accessor: (r) => r.href || '',
            align: 'center',
            render: (href) =>
              href ? (
                <Tooltip title="Open interview">
                  <IconButton
                    size="small"
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener"
                    aria-label="Open interview"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : '—',
            ...W.reasons.nested.open
          }
        ];

        return (
          <DataTable
            title="Interviews with this reason"
            columns={nestedColumns}
            rows={Array.isArray(reasonRow?.instances) ? reasonRow.instances : []}
            dense
          />
        );
      }
    },

    // B) All interviews with this previous solution
    {
      key: 'interviews',
      title: 'All interviews with this previous solution',
      columns: [
        { key: 'name', header: 'Name', ...W.interviews.name },
        { key: 'date', header: 'Date', type: 'date', ...W.interviews.date },
        { key: 'prevSolution', header: 'Previous Solution (raw)', ...W.interviews.prevSolution },
        {
          key: 'reasonsList',
          header: 'Reasons Not Good Enough',
          accessor: (r) => (Array.isArray(r.reasonsList) && r.reasonsList.length ? r.reasonsList.join('\n') : '—'),
          ...W.interviews.reasonsList
        },
        {
          key: 'href',
          header: 'Open',
          accessor: (r) => r.href || '',
          align: 'center',
          render: (href) =>
            href ? (
              <Tooltip title="Open interview">
                <IconButton size="small" component="a" href={href} target="_blank" rel="noopener" aria-label="Open interview">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : '—',
          ...W.interviews.open
        }
      ]
    }
  ];

  return (
    <DataTable
      title={title}
      columns={columns}
      rows={rows}
      subTables={subTables}
      dense
    />
  );
}
